import {
  Command,
  GraphComponent,
  GraphItemTypes,
  GraphViewerInputMode,
  IEdge,
  IGraph,
  ILabelOwner,
  IModelItem,
  INode,
  MoveFocusDirection,
} from '@ardoq/yfiles';
import { isMac, isTruthy } from '@ardoq/common-helpers';
import { t, type I18nLocale } from '@ardoq/i18n';
import { RefObject, useEffect, useRef } from 'react';
import { isEdge, isNode } from './tags';
import type { Edge, GraphAccessibility, InputMode, Node } from './props';
import { toGraphItemTypes } from './yfilesConstructors';
import {
  AccessibleNavigationInputMode,
  isOfItemType,
  levelOf,
} from './AccessibleNavigationInputMode';

/**
 * Key bindings follow yFiles' interaction shortcuts where they overlap:
 * https://docs.yfiles.com/yfiles-html/dguide/interaction-shortcuts/
 */
const ARROW_KEYS: [string, MoveFocusDirection][] = [
  ['ArrowLeft', MoveFocusDirection.LEFT],
  ['ArrowRight', MoveFocusDirection.RIGHT],
  ['ArrowUp', MoveFocusDirection.UP],
  ['ArrowDown', MoveFocusDirection.DOWN],
];

const labelText = (owner: ILabelOwner): string =>
  Array.from(owner.labels, label => label.text)
    .filter(text => text !== '')
    .join(', ');

const describeEdge = (edge: IEdge): string =>
  [labelText(edge.sourceNode), labelText(edge), labelText(edge.targetNode)]
    .filter(text => text !== '')
    .join(' ');

const MAX_ANNOUNCED_CONNECTIONS = 5;

const describeConnections = (
  edges: IEdge[],
  i18nLocale: I18nLocale
): string => {
  const listed = edges.slice(0, MAX_ANNOUNCED_CONNECTIONS).map(describeEdge);
  const hiddenCount = edges.length - listed.length;
  if (hiddenCount === 0) return listed.join('; ');
  return [
    ...listed,
    t(
      i18nLocale,
      'graphView:accessibility.announcement.moreConnections',
      { count: hiddenCount },
      'and {{count}} more'
    ),
  ].join('; ');
};

const describeNode = (
  graph: IGraph,
  node: INode,
  i18nLocale: I18nLocale
): string => {
  const name = labelText(node);
  if (graph.isGroupNode(node)) {
    const count = graph.getChildren(node).size;
    return t(
      i18nLocale,
      'graphView:accessibility.announcement.group',
      { name, count, modifier: groupModifierLabel() },
      count === 1
        ? '{{name}}. Group with 1 item. Press {{modifier}}+Down Arrow to go inside.'
        : '{{name}}. Group with {{count}} items. Press {{modifier}}+Down Arrow to go inside.'
    );
  }
  const edges = Array.from(graph.edgesAt(node));
  if (edges.length === 0) {
    return t(
      i18nLocale,
      'graphView:accessibility.announcement.noConnections',
      { name },
      '{{name}}. No connections.'
    );
  }
  return t(
    i18nLocale,
    'graphView:accessibility.announcement.connections',
    {
      name,
      count: edges.length,
      connections: describeConnections(edges, i18nLocale),
    },
    edges.length === 1
      ? '{{name}}. 1 connection: {{connections}}.'
      : '{{name}}. {{count}} connections: {{connections}}.'
  );
};

const describeLocation = (
  graph: IGraph,
  item: IModelItem,
  i18nLocale: I18nLocale
): string | null => {
  const level = levelOf(graph, item);
  if (!level) return null;
  return t(
    i18nLocale,
    'graphView:accessibility.announcement.inGroup',
    { group: labelText(level), modifier: groupModifierLabel() },
    'In {{group}}. Press {{modifier}}+Up Arrow to go out.'
  );
};

const describeGraphItem = (
  graph: IGraph,
  item: IModelItem,
  i18nLocale: I18nLocale
): string | null => {
  if (item instanceof INode) return describeNode(graph, item, i18nLocale);
  if (item instanceof IEdge) return `${describeEdge(item)}.`;
  return null;
};

type DescribeItemArgs = {
  graph: IGraph;
  item: IModelItem;
  navigableItems: GraphItemTypes;
  i18nLocale: I18nLocale;
};

const describeItem = ({
  graph,
  item,
  navigableItems,
  i18nLocale,
}: DescribeItemArgs): string | null => {
  const description = describeGraphItem(graph, item, i18nLocale);
  if (!description || !(navigableItems & GraphItemTypes.NODE)) {
    return description;
  }
  return [description, describeLocation(graph, item, i18nLocale)]
    .filter(isTruthy)
    .join(' ');
};

const groupModifierLabel = (): string => (isMac() ? 'Option' : 'Alt');

const toNavigationInstruction = (
  navigableItems: GraphItemTypes,
  i18nLocale: I18nLocale
): string => {
  const nodes = Boolean(navigableItems & GraphItemTypes.NODE);
  const edges = Boolean(navigableItems & GraphItemTypes.EDGE);
  if (nodes && edges) {
    return t(
      i18nLocale,
      'graphView:accessibility.instructions.nodesAndEdges',
      'Use the arrow keys to move between nodes and edges.'
    );
  }
  if (edges) {
    return t(
      i18nLocale,
      'graphView:accessibility.instructions.edges',
      'Use the arrow keys to move between edges.'
    );
  }
  return t(
    i18nLocale,
    'graphView:accessibility.instructions.nodes',
    'Use the arrow keys to move between nodes.'
  );
};

const toInstructions = (
  navigableItems: GraphItemTypes,
  i18nLocale: I18nLocale
): string =>
  [
    toNavigationInstruction(navigableItems, i18nLocale),
    navigableItems & GraphItemTypes.NODE
      ? t(
          i18nLocale,
          'graphView:accessibility.instructions.groups',
          { modifier: groupModifierLabel() },
          '{{modifier}}+Down Arrow goes into a group, {{modifier}}+Up Arrow goes back out.'
        )
      : null,
    t(
      i18nLocale,
      'graphView:accessibility.instructions.selection',
      'Space or Enter selects, Escape clears the selection.'
    ),
  ]
    .filter(isTruthy)
    .join(' ');

const announce = (graphComponent: GraphComponent, text: string): void => {
  const liveRegion = graphComponent.ariaLiveRegion;
  liveRegion.textContent = '';
  requestAnimationFrame(() => {
    liveRegion.textContent = text;
  });
};

type UseGraphAccessibilityArgs = {
  graphComponentRef: RefObject<GraphComponent | null>;
  inputModeRef: RefObject<GraphViewerInputMode | null>;
  inputMode: InputMode;
  accessibility: GraphAccessibility | undefined;
};

export const useGraphAccessibility = ({
  graphComponentRef,
  inputModeRef,
  inputMode,
  accessibility,
}: UseGraphAccessibilityArgs): void => {
  const label = accessibility?.label;
  const i18nLocale = accessibility?.i18nLocale;
  const navigableItems = toGraphItemTypes(accessibility?.navigableItems ?? []);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    const viewerInputMode = inputModeRef.current;
    if (!graphComponent || !viewerInputMode || !label || !i18nLocale) return;
    const navigation = viewerInputMode.navigationInputMode;
    if (!(navigation instanceof AccessibleNavigationInputMode)) return;

    const host = graphComponent.htmlElement;
    const focusTarget = host.querySelector<HTMLElement>('[tabindex]') ?? host;
    focusTarget.setAttribute('role', 'application');
    focusTarget.setAttribute(
      'aria-roledescription',
      t(i18nLocale, 'graphView:accessibility.roleDescription', 'graph')
    );
    focusTarget.setAttribute('aria-label', label);

    focusTarget.setAttribute(
      'aria-description',
      toInstructions(navigableItems, i18nLocale)
    );

    const selectCurrentItem = (): boolean => {
      const item = graphComponent.currentItem;
      if (!item || !isOfItemType(item, viewerInputMode.selectableItems)) {
        return false;
      }
      graphComponent.selection.clear();
      graphComponent.selection.add(item);
      announce(
        graphComponent,
        t(
          i18nLocale,
          'graphView:accessibility.announcement.selected',
          'Selected.'
        )
      );
      return true;
    };

    const clearSelection = (): boolean => {
      if (graphComponent.selection.size === 0) return false;
      graphComponent.selection.clear();
      announce(
        graphComponent,
        t(
          i18nLocale,
          'graphView:accessibility.announcement.selectionCleared',
          'Selection cleared.'
        )
      );
      return true;
    };

    const handleGraphKey = (event: KeyboardEvent): boolean => {
      const { key, altKey, ctrlKey, metaKey, shiftKey } = event;
      if (ctrlKey || metaKey || shiftKey) return false;
      if (altKey) {
        if (event.code === 'Digit1') {
          graphComponent.executeCommand(Command.FIT_GRAPH_BOUNDS);
          return true;
        }
        if (event.code === 'Digit3') {
          graphComponent.executeCommand(Command.ZOOM_TO_CURRENT_ITEM);
          return true;
        }
        if (key === 'ArrowDown') {
          navigation.moveFocusTo(MoveFocusDirection.CHILD);
          return true;
        }
        if (key === 'ArrowUp') {
          navigation.moveFocusTo(MoveFocusDirection.PARENT);
          return true;
        }
        return false;
      }
      const direction = ARROW_KEYS.find(([arrowKey]) => arrowKey === key)?.[1];
      if (direction !== undefined) {
        navigation.moveFocusTo(direction);
        return true;
      }
      if (key === ' ') return selectCurrentItem();
      if (key === 'Enter') return selectCurrentItem();
      if (key === 'Escape') return clearSelection();
      return false;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!handleGraphKey(event)) return;
      graphComponent.focusIndicatorManager.usingKeyboard = true;
      event.preventDefault();
      event.stopPropagation();
    };

    const handleCurrentItemChanged = () => {
      if (!host.contains(document.activeElement)) return;
      const item = graphComponent.currentItem;
      if (!item) return;
      const text = describeItem({
        graph: graphComponent.graph,
        item,
        navigableItems,
        i18nLocale,
      });
      if (text) announce(graphComponent, text);
    };

    const handleFocus = (event: FocusEvent) => {
      if (
        event.target instanceof Element &&
        event.target.matches(':focus-visible')
      ) {
        graphComponent.focusIndicatorManager.usingKeyboard = true;
      }
      const current = graphComponent.currentItem;
      if (current && isOfItemType(current, navigableItems)) {
        handleCurrentItemChanged();
        return;
      }
      const firstItem = navigation.firstInReadingOrder(
        navigation.itemsAtLevel(null)
      );
      if (firstItem) graphComponent.currentItem = firstItem;
    };

    graphComponent.addEventListener(
      'current-item-changed',
      handleCurrentItemChanged
    );
    host.addEventListener('focusin', handleFocus);
    host.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      graphComponent.removeEventListener(
        'current-item-changed',
        handleCurrentItemChanged
      );
      host.removeEventListener('focusin', handleFocus);
      host.removeEventListener('keydown', handleKeyDown, { capture: true });
      [
        'role',
        'aria-roledescription',
        'aria-label',
        'aria-description',
      ].forEach(attribute => focusTarget.removeAttribute(attribute));
    };
  }, [
    graphComponentRef,
    inputModeRef,
    inputMode,
    label,
    i18nLocale,
    navigableItems,
  ]);
};

const findItemById = (graph: IGraph, id: string): IModelItem | null =>
  Array.from(graph.nodes).find(
    node => isNode(node) && node.tag.node.id === id
  ) ??
  Array.from(graph.edges).find(
    edge => isEdge(edge) && edge.tag.edge.id === id
  ) ??
  null;

type UseFocusedItemArgs = {
  graphComponentRef: RefObject<GraphComponent | null>;
  focusedId: string | undefined;
  nodes: Node[];
  edges: Edge[];
};

/** Shows the focus indicator on the item with `focusedId`, even while the graph does not have keyboard focus. */
export const useFocusedItem = ({
  graphComponentRef,
  focusedId,
  nodes,
  edges,
}: UseFocusedItemArgs): void => {
  const appliedItemRef = useRef<IModelItem | null>(null);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    if (!graphComponent || !focusedId) {
      appliedItemRef.current = null;
      return;
    }
    const item = findItemById(graphComponent.graph, focusedId);
    if (!item || item === appliedItemRef.current) return;
    appliedItemRef.current = item;
    graphComponent.focusIndicatorManager.showFocusPolicy = 'always';
    graphComponent.currentItem = item;
  }, [graphComponentRef, focusedId, nodes, edges]);

  useEffect(() => {
    const graphComponent = graphComponentRef.current;
    if (!graphComponent || !focusedId) return;
    return () => {
      appliedItemRef.current = null;
      graphComponent.focusIndicatorManager.showFocusPolicy =
        'when-using-keyboard';
    };
  }, [graphComponentRef, focusedId]);
};
